import { useState, useCallback, useEffect } from 'react';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';

/**
 * Custom hook for managing tour state with react-joyride
 * @param {string} tourId - Unique identifier for the tour
 * @returns {object} Tour state and control functions
 */
export const useTour = (tourId) => {
  const storageKey = `tour_completed_${tourId}`;

  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isCompleted, setIsCompleted] = useState(() => {
    return localStorage.getItem(storageKey) === 'true';
  });

  const startTour = useCallback(() => {
    setIsRunning(true);
    setStepIndex(0);
    setCompletedSteps([]);
  }, []);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    setStepIndex(0);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(storageKey);
    setIsCompleted(false);
    setStepIndex(0);
    setCompletedSteps([]);
  }, [storageKey]);

  const completeTour = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
    setIsCompleted(true);
    setIsRunning(false);
    setStepIndex(0);
  }, [storageKey]);

  const goToStep = useCallback((targetIndex) => {
    setStepIndex(targetIndex);
  }, []);

  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    // Handle step completion
    if (type === EVENTS.STEP_AFTER) {
      // Mark current step as completed
      setCompletedSteps((prev) => {
        if (!prev.includes(index)) {
          return [...prev, index];
        }
        return prev;
      });

      // Move to next step
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
      }
    }

    // Handle tour completion or skip
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      completeTour();
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      stopTour();
    }
  }, [completeTour, stopTour]);

  return {
    isRunning,
    isCompleted,
    stepIndex,
    completedSteps,
    startTour,
    stopTour,
    resetTour,
    completeTour,
    handleJoyrideCallback,
    goToStep,
  };
};

/**
 * Hook to determine if a tour should auto-start
 * @param {string} tourId - Unique identifier for the tour
 * @param {boolean} enabled - Whether the tour context is active (e.g., modal is open)
 * @returns {boolean} Whether the tour should auto-start
 */
export const useShouldShowTour = (tourId, enabled = true) => {
  const storageKey = `tour_completed_${tourId}`;
  const isCompleted = localStorage.getItem(storageKey) === 'true';

  return enabled && !isCompleted;
};

/**
 * Custom hook for managing tour state with per-entity tracking (e.g., per-client)
 * @param {string} tourId - Unique identifier for the tour
 * @param {string} entityId - Unique identifier for the entity (e.g., clientId)
 * @returns {object} Tour state and control functions
 */
export const useEntityTour = (tourId, entityId) => {
  const storageKey = entityId ? `tour_completed_${tourId}_${entityId}` : `tour_completed_${tourId}`;

  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isCompleted, setIsCompleted] = useState(() => {
    return localStorage.getItem(storageKey) === 'true';
  });

  // Update completion status when entityId changes
  useEffect(() => {
    setIsCompleted(localStorage.getItem(storageKey) === 'true');
  }, [storageKey]);

  const startTour = useCallback(() => {
    setIsRunning(true);
    setStepIndex(0);
    setCompletedSteps([]);
  }, []);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    setStepIndex(0);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(storageKey);
    setIsCompleted(false);
    setStepIndex(0);
    setCompletedSteps([]);
  }, [storageKey]);

  const completeTour = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
    setIsCompleted(true);
    setIsRunning(false);
    setStepIndex(0);
  }, [storageKey]);

  const goToStep = useCallback((targetIndex) => {
    setStepIndex(targetIndex);
  }, []);

  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    // Handle step completion
    if (type === EVENTS.STEP_AFTER) {
      // Mark current step as completed
      setCompletedSteps((prev) => {
        if (!prev.includes(index)) {
          return [...prev, index];
        }
        return prev;
      });

      // Move to next step
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
      }
    }

    // Handle tour completion or skip
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      completeTour();
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      stopTour();
    }
  }, [completeTour, stopTour]);

  return {
    isRunning,
    isCompleted,
    stepIndex,
    completedSteps,
    startTour,
    stopTour,
    resetTour,
    completeTour,
    handleJoyrideCallback,
    goToStep,
  };
};

/**
 * Hook to determine if an entity-specific tour should auto-start
 * @param {string} tourId - Unique identifier for the tour
 * @param {string} entityId - Unique identifier for the entity (e.g., clientId)
 * @param {boolean} enabled - Whether the tour context is active
 * @returns {boolean} Whether the tour should auto-start
 */
export const useShouldShowEntityTour = (tourId, entityId, enabled = true) => {
  const storageKey = entityId ? `tour_completed_${tourId}_${entityId}` : `tour_completed_${tourId}`;
  const isCompleted = localStorage.getItem(storageKey) === 'true';

  return enabled && entityId && !isCompleted;
};

export default useTour;
